import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useQuery } from '@tanstack/react-query'
import {
  ArrowLeftIcon,
  TrophyIcon,
  CalendarIcon,
  UserIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon
} from '@heroicons/react/24/outline'
import { useAuth0 } from '@auth0/auth0-react'
import { isAdmin } from '../../utils/roles'
import { kazyCupApi, KazyCupWinner } from '../../services/kazyCupApi'
import CreateKazyCupWinnerModal from '../../components/CreateKazyCupWinnerModal'
import EditKazyCupWinnerModal from '../../components/EditKazyCupWinnerModal'
import RetryImage from '../../components/RetryImage'
import toast from 'react-hot-toast'
import { useState, useEffect } from 'react'

export default function KazyCupPage() {
  const { user, getAccessTokenSilently } = useAuth0()
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedWinner, setSelectedWinner] = useState<KazyCupWinner | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  
  const { data: winnersData, isLoading, error, refetch } = useQuery({
    queryKey: ['kazy-cup-winners'],
    queryFn: () => kazyCupApi.getWinners(getAccessTokenSilently),
  })

  useEffect(() => {
    if (error) {
      toast.error('Failed to load Kazy Cup winners')
    }
  }, [error])

  const handleCreateWinner = async (winnerData: any) => {
    try {
      setIsCreating(true)
      await kazyCupApi.createWinner(winnerData, getAccessTokenSilently)
      toast.success('Kazy Cup winner added successfully')
      setShowCreateModal(false)
      refetch() // Refresh the list
    } catch (error: any) {
      toast.error(error.message || 'Failed to add Kazy Cup winner')
    } finally {
      setIsCreating(false)
    }
  }

  const handleEditWinner = (winner: KazyCupWinner) => {
    setSelectedWinner(winner)
    setShowEditModal(true)
  }

  const handleUpdateWinner = async (id: string, winnerData: any) => {
    try {
      setIsUpdating(true)
      await kazyCupApi.updateWinner(id, winnerData, getAccessTokenSilently)
      toast.success('Kazy Cup winner updated successfully')
      setShowEditModal(false)
      setSelectedWinner(null)
      refetch() // Refresh the list
    } catch (error: any) {
      toast.error(error.message || 'Failed to update Kazy Cup winner')
    } finally {
      setIsUpdating(false)
    }
  }

  const handleDeleteWinner = async (winner: KazyCupWinner) => {
    if (!confirm(`Are you sure you want to delete the Kazy Cup record for ${winner.winner} (${winner.year})? This action cannot be undone.`)) {
      return
    }

    try {
      await kazyCupApi.deleteWinner(winner.id, getAccessTokenSilently)
      toast.success('Kazy Cup winner deleted successfully')
      refetch() // Refresh the list
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete Kazy Cup winner')
    }
  }

  return (
    <div className="min-h-screen bg-rb-dark py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          <div className="flex items-center justify-between mb-4">
            <Link
              to="/legacy"
              className="inline-flex items-center text-white hover:text-gray-200 transition-colors"
            >
              <ArrowLeftIcon className="w-5 h-5 mr-2" />
              Back to Legacy
            </Link>
            
            {isAdmin(user) && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center gap-2 bg-rb-gold text-rb-dark px-4 py-2 rounded-lg hover:bg-yellow-400 transition-colors font-medium"
              >
                <PlusIcon className="h-5 w-5" />
                Add Winner
              </button>
            )}
          </div>
          
          <div className="text-center">
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-yellow-500 rounded-full">
                <TrophyIcon className="w-12 h-12 text-white" />
              </div>
            </div>
            <h1 className="text-4xl font-bold text-white mb-4">Kazy Cup Champions</h1>
            <p className="text-xl text-white/70 max-w-3xl mx-auto">
              Honoring exceptional members who have made outstanding contributions to the Midwest Railbirds community.
              Named in honor of our founding member traditions.
            </p>
          </div>
        </motion.div>

        {/* Award Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-white/10 backdrop-blur-sm rounded-xl p-8 mb-12"
        >
          <h2 className="text-2xl font-bold text-white mb-4">About the Kazy Cup</h2>
          <p className="text-white/80 leading-relaxed">
            The Kazy Cup is our annual award recognizing members who embody the spirit of the Midwest Railbirds. 
            Recipients are chosen based on their leadership, community involvement, innovation, and dedication to 
            preserving and enhancing our Derby traditions. This prestigious award has been presented annually 
            since 2008, celebrating those who go above and beyond in service to our community.
          </p>
        </motion.div>

        {/* Winners Timeline */}
        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rb-gold mx-auto"></div>
            <p className="text-white/70 mt-4">Loading Kazy Cup winners...</p>
          </div>
        ) : winnersData?.winners && winnersData.winners.length > 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="space-y-8"
          >
            {winnersData.winners.map((winner, index) => (
            <motion.div
              key={winner.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.1 * index }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden"
            >
              <div className="p-8">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-6 flex-1">
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 bg-yellow-500 rounded-full flex items-center justify-center overflow-hidden">
                        {winner.photo ? (
                          <RetryImage 
                            src={winner.photo} 
                            alt={winner.winner}
                            className="w-full h-full object-cover rounded-full"
                            fallbackIcon={<UserIcon className="w-8 h-8 text-white" />}
                          />
                        ) : (
                          <UserIcon className="w-8 h-8 text-white" />
                        )}
                      </div>
                    </div>
                    
                    <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <CalendarIcon className="w-5 h-5 text-gray-400 mr-2" />
                      <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        {winner.year}
                      </span>
                    </div>
                    
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                      {winner.winner}
                    </h3>
                    
                    <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 mb-4">
                      <TrophyIcon className="w-4 h-4 mr-1" />
                      {winner.achievement}
                    </div>
                    
                    <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                      {winner.description}
                    </p>
                    </div>
                  </div>
                  
                  {/* Admin Actions */}
                  {isAdmin(user) && (
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEditWinner(winner)}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                        title="Edit winner"
                      >
                        <PencilIcon className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDeleteWinner(winner)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                        title="Delete winner"
                      >
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-center py-16"
          >
            <TrophyIcon className="w-16 h-16 text-white/30 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">
              No Kazy Cup Winners Yet
            </h3>
            <p className="text-white/60">
              The first Kazy Cup winners will be recorded here to honor our community champions.
            </p>
          </motion.div>
        )}

        {/* Create Winner Modal */}
        <CreateKazyCupWinnerModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onCreateWinner={handleCreateWinner}
          isLoading={isCreating}
        />

        {/* Edit Winner Modal */}
        <EditKazyCupWinnerModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false)
            setSelectedWinner(null)
          }}
          winner={selectedWinner}
          onUpdateWinner={handleUpdateWinner}
          isLoading={isUpdating}
        />

      </div>
    </div>
  )
}
